import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { User } from './useUser';
import { Experience, FavoriteExperiencesTopicTag } from './useExperiences';
import { axiosGetV3, axiosPost } from '../../api/axios-handler';
import { CallRequest, useCallRequests } from './useCalls';
import useAuth from '../utils/useAuth';

export const peersQueryKeys = {
  recommended: ['peerRecommended'] as const,
  favorites: ['peerFavorites'] as const,
  myConnections: ['peerMyConnections'] as const,
  available: ['peerAvailable'] as const,
  unavailable: ['peerUnavailable'] as const,
  infiniteRecommended: ['infiniteRecommended'] as const,
  peerDetail: (peerID?: number) => ['peerDetail', ...(peerID ? [peerID] : [])] as const,
};

export interface PeerResponse {
  count: number;
  data: Peer[];
}

export interface Peer {
  about_me: string;
  challenge_areas: Tag[];
  client_id: number;
  client_image_url: string;
  client_name?: string | null;
  resource_id: number;
  resource_client_id: number;
  resource_image_url: string;
  display_name: string;
  is_available: boolean;
  is_listener: boolean;
  is_member_following: boolean;
  listener_role_id: number;
  profile_photo_url: string;
  profile_photo_url_square: string;
  tags: Omit<Tag, 'parent_id' | 'key'>[];
  subject_areas: Tag[];
}

export interface Tag {
  id: number;
  name: string;
  tag_type: string;
  parent_id: number;
  key: string;
}

interface PeerRequest {
  favorites?: boolean;
  my_connections?: boolean;
  top_6?: boolean;
  limit?: number;
  page?: number;
}

export const getPeers = async (
  favorite?: boolean,
  myConnections?: boolean,
  top6?: boolean,
  limit?: number,
  page?: number,
): Promise<PeerResponse> => {
  const data: PeerRequest = {};
  if (favorite !== undefined) {
    data['favorites'] = favorite;
  }
  if (myConnections !== undefined) {
    data['my_connections'] = myConnections;
  }
  if (top6 !== undefined) {
    data['top_6'] = top6;
  }
  if (limit !== undefined) {
    data['limit'] = limit;
  }
  if (limit !== undefined && page !== undefined) {
    data['page'] = page;
  }
  return await axiosGetV3(`/peers/recommended`, data).then((peerResponse) => peerResponse.data);
};

export interface InfinitePeersQueryResults extends PeerResponse {
  nextPage?: number;
}

export interface PeerFavoriteResponse {
  id: number;
  type: string;
  listener_role_id: number;
  caller_role_id: number;
}

const addFavoritePeer = async (listenerRoleId: number, callerRoleId?: number): Promise<PeerFavoriteResponse> => {
  const data = {
    caller_role_id: callerRoleId,
    listener_role_id: listenerRoleId,
    type: 'caller_of_listener',
  };

  return await axiosPost(`/favorites/`, data).then((favResponse) => {
    return favResponse.data;
  });
};

export const useAddFavoritePeerWithParams = () => {
  const queryClient = useQueryClient();
  const { user } = useAuth();
  return useMutation({
    mutationFn: (listenerRoleId: number) => addFavoritePeer(listenerRoleId, user?.caller_role_id),
    onMutate: async (listenerRoleId) => {
      // Cancel any outgoing refetches to avoid overwriting optimistic update
      await queryClient.cancelQueries({ queryKey: peersQueryKeys.peerDetail(listenerRoleId) });

      // Snapshot the previous value
      const previousPeerDetail = queryClient.getQueryData<PeerDetail>(peersQueryKeys.peerDetail(listenerRoleId));

      // Optimistically update peerDetail
      if (previousPeerDetail) {
        queryClient.setQueryData<PeerDetail>(peersQueryKeys.peerDetail(listenerRoleId), (old) => {
          if (!old) return old;
          return {
            ...old,
            favorite_id: -1, // Temporary optimistic value, will be replaced with actual id on success
          };
        });
      }

      // Return context with snapshot for rollback
      return { previousPeerDetail };
    },
    onSuccess: (newFav, listenerRoleId, context) => {
      // Update peerDetail with actual favorite_id
      queryClient.setQueryData<PeerDetail>(peersQueryKeys.peerDetail(listenerRoleId), (old) => {
        if (!old) return old;
        return {
          ...old,
          favorite_id: newFav.id,
        };
      });

      queryClient.invalidateQueries(peersQueryKeys.infiniteRecommended);
      queryClient.invalidateQueries(peersQueryKeys.recommended);
      queryClient.invalidateQueries(peersQueryKeys.unavailable);
      queryClient.invalidateQueries(peersQueryKeys.available);
      queryClient.invalidateQueries(peersQueryKeys.favorites);
      queryClient.invalidateQueries(peersQueryKeys.myConnections);
      queryClient.setQueryData<PeerResponse>(peersQueryKeys.favorites, (existingPeerData) => {
        return existingPeerData
          ? {
              count: existingPeerData.count,
              data: existingPeerData.data.map((peer) =>
                peer.listener_role_id === newFav.listener_role_id ? { ...peer, is_member_following: true } : peer,
              ),
            }
          : { count: 0, data: [] };
      });
      queryClient.setQueryData<PeerResponse>(peersQueryKeys.myConnections, (existingPeerData) => {
        return existingPeerData
          ? {
              count: existingPeerData.count,
              data: existingPeerData.data.map((peer) =>
                peer.listener_role_id === newFav.listener_role_id ? { ...peer, is_member_following: true } : peer,
              ),
            }
          : { count: 0, data: [] };
      });
    },
    onError: (error, listenerRoleId, context) => {
      // Rollback optimistic update on error
      if (context?.previousPeerDetail) {
        queryClient.setQueryData<PeerDetail>(peersQueryKeys.peerDetail(listenerRoleId), context.previousPeerDetail);
      }
      queryClient.invalidateQueries(peersQueryKeys.infiniteRecommended);
      queryClient.invalidateQueries(peersQueryKeys.recommended);
    },
  });
};

const removeFavoritePeer = async (listenerRoleId: number, callerRoleId?: number): Promise<number> => {
  return await axiosPost(
    `/peers/${listenerRoleId}/unfavorite`,
    {
      caller_role_id: callerRoleId,
    },
    'v3',
  ).then((_) => listenerRoleId); // the endpoint doesn't return anything but we'll echo listenerRoleId to the onSuccess handler
};

export const useRemoveFavoritePeerWithParams = () => {
  const queryClient = useQueryClient();
  const { user } = useAuth();

  return useMutation({
    mutationFn: (listenerRoleId: number) => removeFavoritePeer(listenerRoleId, user?.caller_role_id),
    onMutate: async (listenerRoleId) => {
      // Cancel any outgoing refetches to avoid overwriting optimistic update
      await queryClient.cancelQueries({ queryKey: peersQueryKeys.peerDetail(listenerRoleId) });

      // Snapshot the previous value
      const previousPeerDetail = queryClient.getQueryData<PeerDetail>(peersQueryKeys.peerDetail(listenerRoleId));

      // Optimistically update peerDetail
      if (previousPeerDetail) {
        queryClient.setQueryData<PeerDetail>(peersQueryKeys.peerDetail(listenerRoleId), (old) => {
          if (!old) return old;
          return {
            ...old,
            favorite_id: null,
          };
        });
      }

      // Return context with snapshot for rollback
      return { previousPeerDetail };
    },
    onSuccess: (listenerRoleId: number) => {
      queryClient.invalidateQueries(peersQueryKeys.infiniteRecommended);
      queryClient.invalidateQueries(peersQueryKeys.recommended);
      queryClient.invalidateQueries(peersQueryKeys.unavailable);
      queryClient.invalidateQueries(peersQueryKeys.available);
      queryClient.invalidateQueries(peersQueryKeys.favorites);
      queryClient.invalidateQueries(peersQueryKeys.myConnections);
      queryClient.setQueryData<PeerResponse>(peersQueryKeys.favorites, (existingPeerData) => {
        return existingPeerData
          ? {
              count: existingPeerData.count,
              data: existingPeerData.data.map((peer) =>
                peer.listener_role_id === listenerRoleId ? { ...peer, is_member_following: false } : peer,
              ),
            }
          : { count: 0, data: [] };
      });
      queryClient.setQueryData<PeerResponse>(peersQueryKeys.myConnections, (existingPeerData) => {
        return existingPeerData
          ? {
              count: existingPeerData.count,
              data: existingPeerData.data.map((peer) =>
                peer.listener_role_id === listenerRoleId ? { ...peer, is_member_following: false } : peer,
              ),
            }
          : { count: 0, data: [] };
      });
    },
    onError: (error, listenerRoleId, context) => {
      // Rollback optimistic update on error
      if (context?.previousPeerDetail) {
        queryClient.setQueryData<PeerDetail>(peersQueryKeys.peerDetail(listenerRoleId), context.previousPeerDetail);
      }
      queryClient.invalidateQueries(peersQueryKeys.infiniteRecommended);
      queryClient.invalidateQueries(peersQueryKeys.recommended);
    },
  });
};

export interface Availability {
  day_of_week: string;
  end_date: string;
  ends_at: string;
  start_date: string;
  starts_at: string;
}

export interface PeerDetailsExperience extends Experience {
  file_url: string;
  is_favorite: boolean;
  rating: number;
  topic_tags: FavoriteExperiencesTopicTag[];
}

export interface PeerDetail {
  available: boolean;
  availabilities: Availability[];
  available_now: boolean;
  background_traits: string[];
  can_take_calls: boolean;
  connection_blocked: boolean;
  favorite_id: number | null;
  id: number;
  is_active: boolean;
  is_peer_active: boolean;
  listener_audio: PeerDetailsExperience[];
  profile_photo_file_url: string;
  profile_photo_square_file_url: string;
  profile_traits: string[];
  secondary_language_tag: string;
  user: User;
  about_me: string;
  challenge_areas: Tag[];
  subject_areas: Tag[];
  client_id: number;
  resource_id: number;
}

export interface PeerDetailResponse {
  data: PeerDetail[];
}

/**
 * Lightweight peer detail hook for the peer-details page.
 * Uses the fast v3 endpoint that only returns essential fields.
 */
export interface PeerDetailLite {
  id: number;
  user: User;
  about_me: string;
  profile_photo_url_square: string;
  can_take_calls: boolean;
  is_peer_active: boolean;
  is_available: boolean;
  available: boolean; // Toggled on/off state (distinct from is_available)
  availabilities: Availability[];
  favorite_id: number | null;
  background_traits: string[];
  profile_traits: string[];
  /**
   * Active Peersona's ChatBot id for this listener, or null if they don't
   * have one. Drives the Peersona CTA on the profile and the post-audio
   * prompt. Surfaced by the backend via SimpleSearchListenerResponseSchema
   * (only chatbots with status=ON are returned).
   */
  chatbot_id?: number | null;
  /** Affiliation fields for KINDLY-1173 profile avatar banner. */
  client_id?: number | null;
  client_name?: string | null;
}

const getPeerDetailLite = async (peerId: number): Promise<PeerDetailLite> => {
  return await axiosGetV3(`/peers/${peerId}`, null).then((peerResponse) => {
    const data = peerResponse.data;
    return {
      ...data,
      id: data.listener_role_id,
    };
  });
};

export const usePeerDetailLite = (peerId?: number) => {
  const { user } = useAuth();
  const { data: callRequests } = useCallRequests(user?.caller_role_id);

  return useQuery<PeerDetailLite>(['peerDetailLite', peerId], () => getPeerDetailLite(peerId!), {
    select: (data) => {
      const pendingRequestListenerIds = callRequests
        ?.filter((request: CallRequest) => request.status === 'backup_pending')
        .map((request: CallRequest) => request.connections[0].listener_role.id);

      return {
        ...data,
        is_available: pendingRequestListenerIds?.includes(data.id) ? false : data.is_available,
      };
    },
    enabled: peerId !== undefined && !!user && !!callRequests,
  });
};
